import LockIcon from "@/assets/colorable/lock.svg?react";
import CustomerChurnView from "@/components/CustomerSuccess/CustomerChurnView";
import CustomerSuccessOverview from "@/components/CustomerSuccess/CustomerSuccessOverview";
import {CUSTOMER_SUCCESS_VIEW_SEARCH_SCHEMA} from "@/components/CustomerSuccess/churnMarkers";
import clsxm from "@/lib/clsxm";
import {createFileRoute, useNavigate} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import {useTranslation} from "react-i18next";
import {useTitle} from "react-use";

export const Route = createFileRoute("/_authenticated/_admin/admins/customer-success")({
  component: CustomerSuccessDashboard,
  validateSearch: zodValidator(CUSTOMER_SUCCESS_VIEW_SEARCH_SCHEMA),
});

function CustomerSuccessDashboard() {
  const {t} = useTranslation("customer_success");

  useTitle(t("title"));

  const navigate = useNavigate();
  const {view} = Route.useSearch();

  return (
    <div className="max-w-wide container mx-auto mt-6 mb-20 flex min-w-0 flex-col gap-6 px-4 sm:mt-8 sm:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="bg-linear-(--primary-dark-gradient) bg-clip-text text-2xl font-bold text-transparent">
              {t("title")}
            </h1>
            <span className="bg-warning-light-yellow text-warning-dark-yellow text-2xs flex items-center gap-1.5 rounded-full px-2.5 py-1 font-semibold">
              <LockIcon aria-hidden="true" className="size-3 shrink-0" />
              {t("internal_badge")}
            </span>
          </div>
        </div>
      </div>
      <div
        aria-label={t("churn_view_label")}
        className="border-neutral-grey-light-dark scrollbar-hide flex min-w-0 gap-6 overflow-x-auto border-b"
        role="tablist">
        {[
          {label: t("churn_overview"), value: "overview"},
          {label: t("churn_title"), value: "churn"},
        ].map(tab => {
          const selected = (view === "churn" ? "churn" : "overview") === tab.value;
          return (
            <button
              key={tab.value}
              aria-selected={selected}
              className={clsxm(
                "text-neutral-grey-dark relative h-9 shrink-0 px-1 text-sm font-medium transition-colors",
                selected && "text-primary-dark-solid",
              )}
              onClick={() =>
                navigate({
                  to: ".",
                  search: prev =>
                    tab.value === "churn"
                      ? {...prev, ordering: undefined, view: "churn"}
                      : {...prev, markers: undefined, ordering: undefined, risk: undefined, view: undefined},
                  replace: true,
                  resetScroll: false,
                })
              }
              role="tab"
              type="button">
              {tab.label}
              <span
                className={clsxm(
                  "bg-neutral-grey-light absolute inset-x-0 bottom-0 h-px",
                  selected && "bg-primary-dark-solid h-0.75 rounded-full",
                )}
              />
            </button>
          );
        })}
      </div>
      {view === "churn" ? <CustomerChurnView /> : <CustomerSuccessOverview />}
    </div>
  );
}
